// src/features/notifications/logic.ts
// Validation, helpers, formatters for notifications admin.
// Framework-agnostic — NO React imports.

import type { ChannelCreateFormState, TemplateFormState, ContactFormState } from './types';

// ============================================
// Notification status map
// ============================================

export const notificationStatusMap: Record<string, { labelKey: string; variant: string }> = {
  NOTIFICATION_STATUS_UNSPECIFIED: { labelKey: 'notifications.statusUnspecified', variant: 'secondary' },
  NOTIFICATION_STATUS_PENDING: { labelKey: 'notifications.statusPending', variant: 'warning' },
  NOTIFICATION_STATUS_SENT: { labelKey: 'notifications.statusSent', variant: 'info' },
  NOTIFICATION_STATUS_DELIVERED: { labelKey: 'notifications.statusDelivered', variant: 'success' },
  NOTIFICATION_STATUS_READ: { labelKey: 'notifications.statusRead', variant: 'success' },
  NOTIFICATION_STATUS_FAILED: { labelKey: 'notifications.statusFailed', variant: 'error' },
  NOTIFICATION_STATUS_CANCELLED: { labelKey: 'notifications.statusCancelled', variant: 'secondary' },
};

export function formatNotificationStatus(status: string): { labelKey: string; variant: string } {
  return notificationStatusMap[status] ?? notificationStatusMap.NOTIFICATION_STATUS_UNSPECIFIED;
}

// ============================================
// Channel type map
// ============================================

export const channelTypeMap: Record<string, { labelKey: string; icon: string }> = {
  NOTIFICATION_CHANNEL_TYPE_UNSPECIFIED: { labelKey: 'notifications.channelUnspecified', icon: 'help-circle' },
  NOTIFICATION_CHANNEL_TYPE_EMAIL: { labelKey: 'notifications.channelEmail', icon: 'mail' },
  NOTIFICATION_CHANNEL_TYPE_SMS: { labelKey: 'notifications.channelSms', icon: 'smartphone' },
  NOTIFICATION_CHANNEL_TYPE_PUSH: { labelKey: 'notifications.channelPush', icon: 'bell' },
  NOTIFICATION_CHANNEL_TYPE_IN_APP: { labelKey: 'notifications.channelInApp', icon: 'inbox' },
  NOTIFICATION_CHANNEL_TYPE_WEBHOOK: { labelKey: 'notifications.channelWebhook', icon: 'link' },
  NOTIFICATION_CHANNEL_TYPE_SLACK: { labelKey: 'notifications.channelSlack', icon: 'hash' },
  NOTIFICATION_CHANNEL_TYPE_DISCORD: { labelKey: 'notifications.channelDiscord', icon: 'headphones' },
  NOTIFICATION_CHANNEL_TYPE_TELEGRAM: { labelKey: 'notifications.channelTelegram', icon: 'send' },
  NOTIFICATION_CHANNEL_TYPE_WHATSAPP: { labelKey: 'notifications.channelWhatsapp', icon: 'message-circle' },
  NOTIFICATION_CHANNEL_TYPE_VK: { labelKey: 'notifications.channelVk', icon: 'users' },
  NOTIFICATION_CHANNEL_TYPE_IN_APP_CHAT: { labelKey: 'notifications.channelInAppChat', icon: 'message-square' },
};

export function formatChannelType(type: string): { labelKey: string; icon: string } {
  return channelTypeMap[type] ?? channelTypeMap.NOTIFICATION_CHANNEL_TYPE_UNSPECIFIED;
}

// ============================================
// Priority map
// ============================================

export const priorityMap: Record<string, { labelKey: string; variant: string }> = {
  NOTIFICATION_PRIORITY_UNSPECIFIED: { labelKey: 'notifications.priorityUnspecified', variant: 'secondary' },
  NOTIFICATION_PRIORITY_LOW: { labelKey: 'notifications.priorityLow', variant: 'secondary' },
  NOTIFICATION_PRIORITY_NORMAL: { labelKey: 'notifications.priorityNormal', variant: 'info' },
  NOTIFICATION_PRIORITY_HIGH: { labelKey: 'notifications.priorityHigh', variant: 'warning' },
  NOTIFICATION_PRIORITY_URGENT: { labelKey: 'notifications.priorityUrgent', variant: 'error' },
};

export function formatPriority(priority: string): { labelKey: string; variant: string } {
  return priorityMap[priority] ?? priorityMap.NOTIFICATION_PRIORITY_UNSPECIFIED;
}

// ============================================
// Category map
// ============================================

export const categoryMap: Record<string, { labelKey: string }> = {
  NOTIFICATION_CATEGORY_UNSPECIFIED: { labelKey: 'notifications.categoryUnspecified' },
  NOTIFICATION_CATEGORY_SECURITY: { labelKey: 'notifications.categorySecurity' },
  NOTIFICATION_CATEGORY_BILLING: { labelKey: 'notifications.categoryBilling' },
  NOTIFICATION_CATEGORY_COLLABORATION: { labelKey: 'notifications.categoryCollaboration' },
  NOTIFICATION_CATEGORY_SYSTEM: { labelKey: 'notifications.categorySystem' },
  NOTIFICATION_CATEGORY_ACCOUNT: { labelKey: 'notifications.categoryAccount' },
  NOTIFICATION_CATEGORY_ACTIVITY: { labelKey: 'notifications.categoryActivity' },
};

export function formatCategory(category: string): { labelKey: string } {
  return categoryMap[category] ?? categoryMap.NOTIFICATION_CATEGORY_UNSPECIFIED;
}

// ============================================
// Channel scope map
// ============================================

export const channelScopeMap: Record<string, { labelKey: string }> = {
  CHANNEL_SCOPE_UNSPECIFIED: { labelKey: 'notifications.scopeUnspecified' },
  CHANNEL_SCOPE_PLATFORM: { labelKey: 'notifications.scopePlatform' },
  CHANNEL_SCOPE_TENANT: { labelKey: 'notifications.scopeTenant' },
  CHANNEL_SCOPE_TEAM: { labelKey: 'notifications.scopeTeam' },
  CHANNEL_SCOPE_USER: { labelKey: 'notifications.scopeUser' },
};

export function formatChannelScope(scope: string): { labelKey: string } {
  return channelScopeMap[scope] ?? channelScopeMap.CHANNEL_SCOPE_UNSPECIFIED;
}

// ============================================
// Channel health map
// ============================================

export const channelHealthMap: Record<string, { labelKey: string; variant: string }> = {
  CHANNEL_HEALTH_STATUS_UNSPECIFIED: { labelKey: 'notifications.healthUnknown', variant: 'secondary' },
  CHANNEL_HEALTH_STATUS_HEALTHY: { labelKey: 'notifications.healthHealthy', variant: 'success' },
  CHANNEL_HEALTH_STATUS_DEGRADED: { labelKey: 'notifications.healthDegraded', variant: 'warning' },
  CHANNEL_HEALTH_STATUS_UNHEALTHY: { labelKey: 'notifications.healthUnhealthy', variant: 'error' },
  CHANNEL_HEALTH_STATUS_UNKNOWN: { labelKey: 'notifications.healthUnknown', variant: 'secondary' },
};

export function formatChannelHealth(status: string): { labelKey: string; variant: string } {
  return channelHealthMap[status] ?? channelHealthMap.CHANNEL_HEALTH_STATUS_UNSPECIFIED;
}

// ============================================
// Validation
// ============================================

export function validateChannelCreate(data: ChannelCreateFormState): Record<string, string> {
  const errors: Record<string, string> = {};
  if (!data.name.trim()) errors.name = 'notifications.channelNameRequired';
  if (!data.type || data.type === 'NOTIFICATION_CHANNEL_TYPE_UNSPECIFIED') errors.type = 'notifications.channelTypeRequired';
  if (!data.scope || data.scope === 'CHANNEL_SCOPE_UNSPECIFIED') errors.scope = 'notifications.channelScopeRequired';

  if (data.type === 'NOTIFICATION_CHANNEL_TYPE_EMAIL') {
    if (!data.smtpHost.trim()) errors.smtpHost = 'notifications.smtpHostRequired';
    if (!data.fromEmail.trim()) errors.fromEmail = 'notifications.fromEmailRequired';
  }
  if (data.type === 'NOTIFICATION_CHANNEL_TYPE_TELEGRAM') {
    if (!data.telegramBotToken.trim()) errors.telegramBotToken = 'notifications.botTokenRequired';
  }
  if (data.type === 'NOTIFICATION_CHANNEL_TYPE_SLACK') {
    if (!data.slackBotToken.trim() && !data.slackWebhookUrl.trim()) errors.slackBotToken = 'notifications.slackCredentialsRequired';
  }
  if (data.type === 'NOTIFICATION_CHANNEL_TYPE_WEBHOOK') {
    if (!data.webhookUrl.trim()) errors.webhookUrl = 'notifications.webhookUrlRequired';
  }

  return errors;
}

export function validateTemplateForm(data: TemplateFormState): Record<string, string> {
  const errors: Record<string, string> = {};
  if (!data.name.trim()) errors.name = 'notifications.templateNameRequired';
  if (!data.channelType || data.channelType === 'NOTIFICATION_CHANNEL_TYPE_UNSPECIFIED') errors.channelType = 'notifications.channelTypeRequired';
  if (!data.language.trim()) errors.language = 'notifications.languageRequired';
  if (!data.subjectTemplate.trim()) errors.subjectTemplate = 'notifications.subjectTemplateRequired';
  if (!data.bodyTemplate.trim()) errors.bodyTemplate = 'notifications.bodyTemplateRequired';
  return errors;
}

// ============================================
// Formatting helpers
// ============================================

export function formatDeliveryAttempt(attempt: { success: boolean; error_message?: string; provider_response?: string; attempted_at?: string }): { statusKey: string; variant: string } {
  return attempt.success
    ? { statusKey: 'notifications.deliverySuccess', variant: 'success' }
    : { statusKey: 'notifications.deliveryFailed', variant: 'error' };
}

// ============================================
// Contact type map
// ============================================

export const contactTypeMap: Record<string, { labelKey: string; icon: string }> = {
  CONTACT_TYPE_UNSPECIFIED: { labelKey: 'notifications.contactTypeUnspecified', icon: 'help-circle' },
  CONTACT_TYPE_EMAIL: { labelKey: 'notifications.contactTypeEmail', icon: 'mail' },
  CONTACT_TYPE_PHONE: { labelKey: 'notifications.contactTypePhone', icon: 'phone' },
  CONTACT_TYPE_ADDRESS: { labelKey: 'notifications.contactTypeAddress', icon: 'map-pin' },
  CONTACT_TYPE_SOCIAL: { labelKey: 'notifications.contactTypeSocial', icon: 'globe' },
  CONTACT_TYPE_TELEGRAM: { labelKey: 'notifications.contactTypeTelegram', icon: 'send' },
  CONTACT_TYPE_WHATSAPP: { labelKey: 'notifications.contactTypeWhatsapp', icon: 'message-circle' },
  CONTACT_TYPE_VK: { labelKey: 'notifications.contactTypeVk', icon: 'users' },
  CONTACT_TYPE_VIBER: { labelKey: 'notifications.contactTypeViber', icon: 'smartphone' },
  CONTACT_TYPE_SLACK: { labelKey: 'notifications.contactTypeSlack', icon: 'hash' },
  CONTACT_TYPE_DISCORD: { labelKey: 'notifications.contactTypeDiscord', icon: 'headphones' },
};

export function formatContactType(type: string): { labelKey: string; icon: string } {
  return contactTypeMap[type] ?? contactTypeMap.CONTACT_TYPE_UNSPECIFIED;
}

// ============================================
// Digest frequency map
// ============================================

export const digestFrequencyMap: Record<string, { labelKey: string }> = {
  DIGEST_FREQUENCY_UNSPECIFIED: { labelKey: 'notifications.digestUnspecified' },
  DIGEST_FREQUENCY_NEVER: { labelKey: 'notifications.digestNever' },
  DIGEST_FREQUENCY_IMMEDIATELY: { labelKey: 'notifications.digestImmediately' },
  DIGEST_FREQUENCY_HOURLY: { labelKey: 'notifications.digestHourly' },
  DIGEST_FREQUENCY_DAILY: { labelKey: 'notifications.digestDaily' },
  DIGEST_FREQUENCY_WEEKLY: { labelKey: 'notifications.digestWeekly' },
  DIGEST_FREQUENCY_MONTHLY: { labelKey: 'notifications.digestMonthly' },
};

export function formatDigestFrequency(freq: string): { labelKey: string } {
  return digestFrequencyMap[freq] ?? digestFrequencyMap.DIGEST_FREQUENCY_UNSPECIFIED;
}

// ============================================
// Test result map
// ============================================

export const testResultMap: Record<string, { labelKey: string; variant: string }> = {
  TEST_RESULT_UNSPECIFIED: { labelKey: 'notifications.testUnspecified', variant: 'secondary' },
  TEST_RESULT_SUCCESS: { labelKey: 'notifications.testSuccess', variant: 'success' },
  TEST_RESULT_CONNECTION_FAILED: { labelKey: 'notifications.testConnectionFailed', variant: 'error' },
  TEST_RESULT_AUTHENTICATION_FAILED: { labelKey: 'notifications.testAuthFailed', variant: 'error' },
  TEST_RESULT_INVALID_CONFIG: { labelKey: 'notifications.testInvalidConfig', variant: 'error' },
  TEST_RESULT_RATE_LIMITED: { labelKey: 'notifications.testRateLimited', variant: 'warning' },
  TEST_RESULT_RECIPIENT_INVALID: { labelKey: 'notifications.testRecipientInvalid', variant: 'error' },
  TEST_RESULT_UNKNOWN_ERROR: { labelKey: 'notifications.testUnknownError', variant: 'error' },
};

export function formatTestResult(result: string): { labelKey: string; variant: string } {
  return testResultMap[result] ?? testResultMap.TEST_RESULT_UNSPECIFIED;
}

// ============================================
// Contact validation
// ============================================

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^\+?[\d\s()-]{7,}$/;

export function validateContactForm(data: ContactFormState): Record<string, string> {
  const errors: Record<string, string> = {};
  if (!data.type || data.type === 'CONTACT_TYPE_UNSPECIFIED') errors.type = 'notifications.contactTypeRequired';
  if (!data.value.trim()) errors.value = 'notifications.contactValueRequired';
  else if (data.type === 'CONTACT_TYPE_EMAIL' && !EMAIL_RE.test(data.value)) errors.value = 'notifications.contactEmailInvalid';
  else if (data.type === 'CONTACT_TYPE_PHONE' && !PHONE_RE.test(data.value)) errors.value = 'notifications.contactPhoneInvalid';
  return errors;
}

// ============================================
// Relative time formatter
// ============================================

export function formatRelativeTime(ts: unknown): string {
  if (!ts) return '';
  const date = typeof ts === 'object' && ts !== null && 'seconds' in ts
    ? new Date((ts as { seconds: number }).seconds * 1000)
    : new Date(ts as string | number);
  if (isNaN(date.getTime())) return '';
  const now = Date.now();
  const diff = now - date.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return '< 1m';
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;
  return date.toLocaleDateString();
}

// ============================================
// Sub-navigation items for notifications section
// ============================================

export type NotificationTab = 'feed' | 'channels' | 'templates' | 'scheduled' | 'contacts' | 'analytics' | 'preferences';

export const notificationTabs: { id: NotificationTab; labelKey: string; adminOnly?: boolean }[] = [
  { id: 'feed', labelKey: 'notifications.list' },
  { id: 'channels', labelKey: 'notifications.channels', adminOnly: true },
  { id: 'templates', labelKey: 'notifications.templates', adminOnly: true },
  { id: 'scheduled', labelKey: 'notifications.scheduled', adminOnly: true },
  { id: 'contacts', labelKey: 'notifications.contacts', adminOnly: true },
  { id: 'analytics', labelKey: 'notifications.analytics', adminOnly: true },
  { id: 'preferences', labelKey: 'notifications.preferences' },
];

// ============================================
// Status filter options
// ============================================

export const notificationStatusFilters = [
  { key: '', labelKey: 'common.all' },
  { key: 'NOTIFICATION_STATUS_PENDING', labelKey: 'notifications.statusPending' },
  { key: 'NOTIFICATION_STATUS_SENT', labelKey: 'notifications.statusSent' },
  { key: 'NOTIFICATION_STATUS_DELIVERED', labelKey: 'notifications.statusDelivered' },
  { key: 'NOTIFICATION_STATUS_READ', labelKey: 'notifications.statusRead' },
  { key: 'NOTIFICATION_STATUS_FAILED', labelKey: 'notifications.statusFailed' },
] as const;

// ============================================
// Notification display helpers
// ============================================

export function isNotificationUnread(status?: string): boolean {
  return status !== 'NOTIFICATION_STATUS_READ';
}

export function getChannelIconColor(type?: string): string {
  switch (type) {
    case 'NOTIFICATION_CHANNEL_TYPE_EMAIL': return 'bg-blue-500/10 text-blue-600 dark:text-blue-400';
    case 'NOTIFICATION_CHANNEL_TYPE_SMS': return 'bg-green-500/10 text-green-600 dark:text-green-400';
    case 'NOTIFICATION_CHANNEL_TYPE_PUSH': return 'bg-amber-500/10 text-amber-600 dark:text-amber-400';
    case 'NOTIFICATION_CHANNEL_TYPE_IN_APP': return 'bg-purple-500/10 text-purple-600 dark:text-purple-400';
    case 'NOTIFICATION_CHANNEL_TYPE_TELEGRAM': return 'bg-sky-500/10 text-sky-600 dark:text-sky-400';
    case 'NOTIFICATION_CHANNEL_TYPE_SLACK': return 'bg-pink-500/10 text-pink-600 dark:text-pink-400';
    case 'NOTIFICATION_CHANNEL_TYPE_WEBHOOK': return 'bg-orange-500/10 text-orange-600 dark:text-orange-400';
    default: return 'bg-muted text-muted-foreground';
  }
}

// ============================================
// Days of week helpers
// ============================================

export const daysOfWeek = [
  { value: 0, labelKey: 'notifications.daySunday' },
  { value: 1, labelKey: 'notifications.dayMonday' },
  { value: 2, labelKey: 'notifications.dayTuesday' },
  { value: 3, labelKey: 'notifications.dayWednesday' },
  { value: 4, labelKey: 'notifications.dayThursday' },
  { value: 5, labelKey: 'notifications.dayFriday' },
  { value: 6, labelKey: 'notifications.daySaturday' },
];

// ============================================
// Template variable placeholders ({{var}})
// ============================================

const PLACEHOLDER_RE = /\{\{\s*([\w.]+)\s*\}\}/g;

/** Extract distinct `{{variable}}` names referenced in template text, in first-seen order. */
export function extractTemplatePlaceholders(...texts: string[]): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const text of texts) {
    if (!text) continue;
    for (const match of text.matchAll(PLACEHOLDER_RE)) {
      const name = match[1];
      if (!seen.has(name)) {
        seen.add(name);
        result.push(name);
      }
    }
  }
  return result;
}

export function getChannelConfigKey(type: string): string | null {
  const map: Record<string, string> = {
    NOTIFICATION_CHANNEL_TYPE_EMAIL: 'email_config',
    NOTIFICATION_CHANNEL_TYPE_SMS: 'sms_config',
    NOTIFICATION_CHANNEL_TYPE_TELEGRAM: 'telegram_config',
    NOTIFICATION_CHANNEL_TYPE_WHATSAPP: 'whatsapp_config',
    NOTIFICATION_CHANNEL_TYPE_SLACK: 'slack_config',
    NOTIFICATION_CHANNEL_TYPE_DISCORD: 'discord_config',
    NOTIFICATION_CHANNEL_TYPE_PUSH: 'push_config',
    NOTIFICATION_CHANNEL_TYPE_WEBHOOK: 'webhook_config',
    NOTIFICATION_CHANNEL_TYPE_VK: 'vk_config',
    NOTIFICATION_CHANNEL_TYPE_IN_APP_CHAT: 'in_app_chat_config',
  };
  return map[type] ?? null;
}
